import { enableDebug } from '@sitecore-jss/sitecore-jss-react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import AppRoot from './AppRoot';
import i18ninit from './i18n';
import GraphQLClientFactory from './lib/GraphQLClientFactory';
import config from './temp/config';

// Hydration error suppression is now in public/index.html
// to ensure it runs BEFORE React loads (no race condition).

if (import.meta.env.VITE_DEBUG) {
  enableDebug(import.meta.env.VITE_DEBUG);
}

/* eslint-disable no-underscore-dangle */

let renderFunction = (rootElement, component) => {
  const root = createRoot(rootElement);
  root.render(component);
  return root;
};

let initLanguage = config.defaultLanguage;

/*
  SSR Data
  If we're running in a server-side rendering scenario,
  the server will provide JSON in the #__JSS_STATE__ element
  for us to acquire the initial state to run with on the client.

  This enables us to skip a network request to load up the layout data.
  We are emitting a quiescent script with JSON so that we can take advantage
  of JSON.parse()'s speed advantage over parsing full JS, and enable
  working without needing `unsafe-inline` in Content Security Policies.

  SSR is initiated from /server/server.js.
*/
let __JSS_STATE__ = null;
const ssrRawJson = document.getElementById('__JSS_STATE__');
if (ssrRawJson) {
  __JSS_STATE__ = JSON.parse(ssrRawJson.innerHTML);
}
let isPageEditing = false;
if (__JSS_STATE__) {
  // In Experience Editor mode (pageEditing: true), use createRoot instead of hydrateRoot.
  // EE renders components server-side via .NET without styled-components SSR support.
  // React 19's hydrateRoot is aggressive about hydration mismatches — it bails out hard
  // from mismatched subtrees, which prevents styled-components from applying styles
  // after EE inserts new components into the page.
  isPageEditing = __JSS_STATE__.sitecore?.context?.pageEditing === true;
  if (!isPageEditing) {
    // when React initializes from a SSR-based initial state, you need to render with `hydrateRoot` instead of `render`
    renderFunction = hydrateRoot;
  }

  // set i18n language SSR state language instead of static config default language
  initLanguage = __JSS_STATE__.sitecore.context.language;
}

/*
  GraphQL Data
  The Apollo Client needs to be initialized to make GraphQL available to the JSS app.
  Not using GraphQL? Remove this, and the ApolloContext from `AppRoot`.
*/
// Apollo supports SSR of GraphQL queries, so like JSS SSR, it has an object we can pre-hydrate the client cache from
// to avoid needing to re-run GraphQL queries after the SSR page loads
const initialGraphQLState = __JSS_STATE__ && __JSS_STATE__.APOLLO_STATE ? __JSS_STATE__.APOLLO_STATE : null;

const graphQLClient = GraphQLClientFactory(config.graphQLEndpoint, false, initialGraphQLState);

/*
  App Rendering
*/
// initialize the dictionary, then render the app
// note: if not making a multlingual app, the dictionary init can be removed.
// Use SSR dictionary if available to avoid client-side fetch
const dictionary = __JSS_STATE__?.dictionary;
i18ninit(initLanguage, dictionary).then(() => {
  // HTML element to place the app into
  const rootElement = document.getElementById('root');

  const appComponent = (
    <HelmetProvider>
      <AppRoot
        path={window.location.pathname}
        Router={BrowserRouter}
        graphQLClient={graphQLClient}
        ssrState={__JSS_STATE__}
      />
    </HelmetProvider>
  );

  renderFunction(rootElement, appComponent);
});
